import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, ClipboardList, ShieldCheck, Stethoscope } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const Preparacion = () => {
  const lang = useLanguage();
  const t = getPageTexts('preparacion', lang);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const categories = [
    { title: t.cat1Title, items: t.cat1Items || [] },
    { title: t.cat2Title, items: t.cat2Items || [] },
    { title: t.cat3Title, items: t.cat3Items || [] },
    { title: t.cat4Title, items: t.cat4Items || [] },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.badge}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1C1C1A] mb-6 leading-tight">
                {t.title1}<br /><span className="italic text-[#B8975A]">{t.title2}</span>
              </h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-base md:text-lg text-[#4A4845] leading-relaxed mb-8">{t.heroDesc}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAssessment} <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} /> {t.callNow}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="/images/prep-closeup.jpg" alt={t.title1} className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why prepare */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.conceptLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">{t.whyTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: ShieldCheck, title: t.reason1Title, desc: t.reason1Desc },
              { icon: Stethoscope, title: t.reason2Title, desc: t.reason2Desc },
              { icon: ClipboardList, title: t.reason3Title, desc: t.reason3Desc },
            ].map((item, i) => (
              <div key={i} className="p-8 border border-[#B8975A]/30 hover:border-[#B8975A] transition-colors text-center">
                <item.icon size={40} className="text-[#B8975A] mx-auto mb-6" />
                <h3 className="font-serif text-xl md:text-2xl text-white mb-4">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.protocolLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.phasesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '01', title: t.phase1Title, items: t.phase1Items || [] },
              { num: '02', title: t.phase2Title, items: t.phase2Items || [] },
              { num: '03', title: t.phase3Title, items: t.phase3Items || [] },
            ].map((phase, i) => (
              <div key={i} className="bg-white p-8 border-t-2 border-[#B8975A]">
                <span className="text-[#B8975A] font-serif text-3xl">{phase.num}</span>
                <h3 className="font-serif text-xl text-[#1C1C1A] mt-4 mb-3">{phase.title}</h3>
                <ul className="space-y-2 text-[#4A4845] text-sm">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.specificLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.adaptedTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, i) => (
              <div key={i} className="bg-[#F0EDE7] p-6">
                <h3 className="font-serif text-lg text-[#1C1C1A] mb-4 pb-3 border-b border-[#B8975A]/30">{cat.title}</h3>
                <ul className="space-y-2">
                  {cat.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-[#4A4845]">
                      <span className="w-1.5 h-1.5 bg-[#B8975A] rounded-full flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#8A8780] mt-8">{t.eachProtocol}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t.ctaTitle}</h2>
          <p className="text-white/70 mb-10">{t.ctaDesc}</p>
          <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
            {t.requestAssessment} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Preparacion;
